/* ==========================================================================
   Michael Vardani — header reveal on scroll up

   The sticky header tucks itself out of the way while the visitor is reading
   down the page, and drops back into view the moment they scroll up — in the
   spot they are already in, so the primary menu is reachable from anywhere on
   a long page without scrolling all the way back to the top.

   One document-level scroll listener for the whole site. The configuration is
   read off the <mv-header> element rather than baked in here, and re-read on
   shopify:section:load, because a <script src> only ever executes once: a
   theme-editor section reload swaps the markup out from underneath us.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__mvHeaderReveal) return;
  window.__mvHeaderReveal = true;

  var TUCKED = 'mv-header--tucked';
  var HOST_TUCKED = 'mv-header-section--tucked';
  var HOST_PINNED = 'mv-header-section--pinned';
  var FLOATING = 'mv-header--floating';
  var NOFX = 'mv-header--nofx';
  var MOBILE_MAX = 989; /* matches the header breakpoint in custom-theme.css */

  var header = null;
  var host = null; /* the Shopify section wrapper — the element that sticks */
  var conf = null;
  var lastY = 0;
  var runDown = 0;
  var runUp = 0;
  var wasSolid = null; /* overlay only: pinned to the viewport, or still in flow */
  var ticking = false;
  var editorHold = false;

  function toInt(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  /* Clamped so iOS rubber-banding past either end cannot register as a
     direction change and flap the header. */
  function scrollY() {
    var y = window.pageYOffset || document.documentElement.scrollTop || 0;
    var max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    return Math.min(Math.max(y, 0), max);
  }

  /* On the home page the header may also be floating transparently over the
     hero, which this script drives as well — so a header carrying only the
     overlay flag still has to be found. */
  function read() {
    header = document.querySelector('mv-header[data-mv-reveal], mv-header[data-mv-overlay]');
    if (!header) {
      conf = null;
      host = null;
      return;
    }
    /* The sticky element is the section wrapper, not the header itself — the
       header slides inside it. While the header is tucked the wrapper is still
       a full-height invisible strip across the top, so it has to stop
       swallowing clicks meant for the page underneath. */
    host = header.closest('.mv-header-section') || header.parentElement;
    conf = {
      reveal: header.hasAttribute('data-mv-reveal'),
      overlay: header.hasAttribute('data-mv-overlay'),
      scope: header.dataset.mvRevealScope || 'all',
      offset: toInt(header.dataset.mvRevealOffset, 160),
      down: toInt(header.dataset.mvRevealDown, 14),
      up: toInt(header.dataset.mvRevealUp, 8),
    };
    lastY = scrollY();
    runDown = 0;
    runUp = 0;
    wasSolid = null;
    apply();
  }

  function inScope() {
    if (!conf) return false;
    if (conf.scope === 'desktop') return window.innerWidth > MOBILE_MAX;
    if (conf.scope === 'mobile') return window.innerWidth <= MOBILE_MAX;
    return true;
  }

  /* Anything that would be cut off, or that has taken over the screen, pins
     the header in place: the search row lives inside it, and the mobile menu
     and wishlist drawer both lock the page behind a scrim. */
  function held() {
    if (editorHold || !header) return true;
    if (header.contains(document.activeElement)) return true;
    var search = header.querySelector('[data-mv-search-panel]');
    if (search && !search.hidden) return true;
    if (document.querySelector('[data-mv-mobile-nav].is-open')) return true;
    if (document.querySelector('mv-wishlist-drawer.is-open')) return true;
    return false;
  }

  function show() {
    if (header) header.classList.remove(TUCKED);
    if (host) host.classList.remove(HOST_TUCKED);
  }

  function tuck() {
    if (header) header.classList.add(TUCKED);
    if (host) host.classList.add(HOST_TUCKED);
  }

  function apply() {
    if (!conf || !header) return;

    var y = scrollY();
    var scoped = inScope();

    /* See-through while the shopper is still on the hero, where the bar is an
       ordinary in-flow element and scrolls away with the picture. Past the
       reveal offset it becomes the opaque menu pinned to the viewport — which
       is also the point at which it starts hiding itself.

       Nothing is positioned from here: the stylesheet owns both states, and
       this only says which one is in force. */
    if (conf.overlay && host) {
      var solid = y > conf.offset;

      /* The hand-over between the flow and the viewport. Going down, the bar
         starts out of sight so it cannot flash across the screen on the way;
         going back up it is already where the reveal wants it. Either way the
         animations are off for the frame in which the jump happens. */
      if (solid !== wasSolid) {
        wasSolid = solid;
        header.classList.add(NOFX);
        if (solid && y >= lastY) tuck();
        else show();
        window.requestAnimationFrame(function () {
          header.classList.remove(NOFX);
        });
      }

      host.classList.toggle(HOST_PINNED, solid);
      if (!solid) {
        show();
        header.classList.remove(FLOATING);
        runDown = 0;
        runUp = 0;
        lastY = y;
        return;
      }
    }

    header.classList.toggle(FLOATING, scoped && y > conf.offset);

    /* Overlay without the reveal: the bar pins and stays put. */
    if (!conf.reveal) {
      show();
      lastY = y;
      return;
    }

    if (!scoped || held()) {
      show();
      runDown = 0;
      runUp = 0;
      lastY = y;
      return;
    }

    var delta = y - lastY;
    lastY = y;

    /* Near the top the header always stays put — there is nothing above it to
       scroll back to. */
    if (y <= conf.offset) {
      show();
      runDown = 0;
      runUp = 0;
      return;
    }

    /* Direction is measured as a run, not a single frame, so a trackpad jitter
       or the tail of a momentum scroll does not toggle it. */
    if (delta > 0) {
      runUp = 0;
      runDown += delta;
      if (runDown >= conf.down) tuck();
    } else if (delta < 0) {
      runDown = 0;
      runUp -= delta;
      if (runUp >= conf.up) show();
    }
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      ticking = false;
      apply();
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', function () {
    runDown = 0;
    runUp = 0;
    show();
    onScroll();
  }, { passive: true });

  /* Webfonts landing can shift the announcement bar by a pixel or two, which
     moves the gap the floating menu leaves for it. */
  window.addEventListener('load', onScroll);

  /* A keyboard user tabbing into the header, or any in-page anchor jump,
     should never land behind a hidden bar. */
  document.addEventListener('focusin', function (event) {
    if (header && header.contains(event.target)) show();
  });
  window.addEventListener('hashchange', show);

  document.addEventListener('shopify:section:load', read);
  document.addEventListener('shopify:section:unload', read);

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:select', function () {
      editorHold = true;
      show();
    });
    document.addEventListener('shopify:block:select', function () {
      editorHold = true;
      show();
    });
    document.addEventListener('shopify:section:deselect', function () {
      editorHold = false;
    });
    document.addEventListener('shopify:block:deselect', function () {
      editorHold = false;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', read);
  } else {
    read();
  }
})();
